import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import {
  Divider,
  Input,
  Form,
  Col,
  Button,
  Row,
  message,
  Tooltip,
  Image,
} from 'antd';
import { useTranslation } from 'react-i18next';
import LogoUpload from '@components/LogoUpload';
import api from '@services/api';
import SessionContext from '@providers/Session/SessionContext';
import KeycloakContext from '@providers/Keycloak/KeycloakContext';
import { removeEmptyAttributes } from '@utils/removeEmptyAttributes';
import { InfoCircleOutlined } from '@ant-design/icons';
import CustomCard from '@components/CustomCard';
import { mutate } from 'swr';
import ImageUpload from '@components/ImageUpload';
import { getBase64Image } from '@utils/formatters';
import RoleChecker from '@components/RoleChecker';
import rolesConstants from '@utils/rolesConstants';
import SendEmails from './SendEmails';

const { Item } = Form;

type PersonalizationData = {
  file?: any;
  mainColor?: string;
  domain?: string;
  whatsapp?: string;
  whatsappMessage?: string;
};

const Personalization = () => {
  const { t } = useTranslation('main');
  const [form] = Form.useForm();
  const whatsappNumber = Form.useWatch('whatsapp', form);
  const {
    theme,
    customer: { customerLogged },
  } = useContext(SessionContext);
  const { keycloak } = useContext(KeycloakContext);
  const customerId = keycloak?.idTokenParsed?.customerId;
  const [mainColor, setMainColor] = useState<string>();
  const [darkColor, setDarkColor] = useState<string>();
  const [file, setFile] = useState();
  const [darkFile, setDarkFile] = useState();
  const [backgroundFile, setBackgroundFile] = useState();
  const [isSaving, setIsSaving] = useState(false);
  const [bgImgUrl, setBgImgUrl] = useState<string>();

  const customerPersonalization = useCallback(
    (formData: any) => api.put(`/customers/customizationTheme/`, formData),
    [],
  );

  const domainProtocol = useMemo(() => {
    const { protocol, hostname } = window.location;
    return hostname === 'localhost' ? 'https:' : protocol;
  }, []);

  useEffect(() => {
    if (customerLogged) {
      setMainColor(customerLogged?.mainColor);
      setDarkColor(customerLogged?.darkMainColor);

      form.setFieldsValue({
        whatsapp: customerLogged?.whatsapp,
        whatsappMessage: customerLogged?.whatsappMessage,
      });

      if (customerLogged?.domain) {
        const domain = `${domainProtocol}//${customerLogged?.domain}`;

        form.setFieldsValue({
          domain,
        });
      }

      const { protocol, hostname } = window.location;
      const isLocalhost = hostname === 'localhost';
      const localhostHostname = process.env.REACT_APP_HML_HOSTNAME;

      const url = `${isLocalhost ? 'https:' : protocol}//${
        isLocalhost ? localhostHostname : hostname
      }/images/${customerLogged?.domain}-bg`;

      getBase64Image(url, img => setBgImgUrl(img));
    }
  }, [form, customerLogged, domainProtocol]);

  const hardRefresh = useCallback(() => {
    const { href } = window.location;
    window.location.href = `${href}?cache_buster=${new Date().getTime()}`;
  }, []);

  const handlePersonalize = useCallback(async () => {
    try {
      setIsSaving(true);

      const formData = new FormData();
      if (customerId) {
        formData.append('customerId', String(customerId));
      }

      const domain = new URL(form.getFieldValue('domain'))?.hostname;

      const personalizationData: PersonalizationData = removeEmptyAttributes({
        file,
        darkFile,
        mainColor,
        darkColor,
        backgroundFile,
        domain,
        whatsapp: form.getFieldValue('whatsapp'),
        whatsappMessage: form.getFieldValue('whatsappMessage'),
      });

      Object.entries(personalizationData).forEach(([name, value]) =>
        formData.append(name, value),
      );

      await customerPersonalization(formData);

      const { hostname } = window.location;
      const currentHostname =
        hostname === 'localhost'
          ? process.env.REACT_APP_HML_HOSTNAME
          : hostname;

      if (currentHostname === domain) {
        hardRefresh();
      }
      mutate('/customers/self');
      message.success(t('personalization.successful-submit'));
    } catch {
      message.error(t('personalization.failed-submit'));
    }
    setIsSaving(false);
  }, [
    customerId,
    form,
    file,
    darkFile,
    mainColor,
    darkColor,
    backgroundFile,
    customerPersonalization,
    t,
    hardRefresh,
  ]);

  const handleBackgroundFile = useCallback((bgFile: any) => {
    setBackgroundFile(bgFile);

    setBgImgUrl(URL.createObjectURL(bgFile));
  }, []);

  return (
    <>
      <CustomCard
        title={t('personalization.title')}
        bodyStyle={{ padding: 40 }}
      >
        <p
          style={{
            maxWidth: 520,
            margin: '0 auto',
            textAlign: 'center',
          }}
        >
          {t('personalization.description')}
        </p>
        <Row gutter={[16, 16]} justify="center">
          <Divider style={{ marginTop: 40 }}>
            {t('personalization.light-theme')}
            <Tooltip title={t('personalization.light-theme-help')}>
              <Button type="link" icon={<InfoCircleOutlined />} />
            </Tooltip>
          </Divider>
          <Col span={16}>
            <LogoUpload
              type="light"
              setColor={setMainColor}
              setFile={setFile}
            />
          </Col>
          <Divider style={{ marginTop: 40 }}>
            {t('personalization.dark-theme')}
            <Tooltip title={t('personalization.dark-theme-help')}>
              <Button type="link" icon={<InfoCircleOutlined />} />
            </Tooltip>
          </Divider>
          <Col span={16}>
            <LogoUpload
              type="dark"
              setColor={setDarkColor}
              setFile={setDarkFile}
            />
          </Col>
          <Divider style={{ marginTop: 40 }}>
            {t('personalization.login-background')}
          </Divider>
          <Col span={16} style={{ textAlign: 'center' }}>
            <ImageUpload
              allowCut
              onChange={handleBackgroundFile}
              dimensions={{
                width: 1400,
                height: 800,
              }}
            />
            <Image
              src={bgImgUrl}
              preview={false}
              style={{
                borderRadius: theme?.shapes?.borderRadius,
                width: 400,
                marginTop: 16,
              }}
            />
          </Col>
        </Row>
        <Divider style={{ margin: '40px 0' }} />
        <Form
          form={form}
          id="personalizationForm"
          layout="vertical"
          onFinish={handlePersonalize}
        >
          <Row gutter={[16, 16]} justify="center">
            <Col span={16}>
              <Item
                label={
                  <>
                    {t('personalization.domain')}
                    <Tooltip title={t('personalization.domain-help')}>
                      <Button type="link" icon={<InfoCircleOutlined />} />
                    </Tooltip>
                  </>
                }
                name="domain"
                rules={[
                  {
                    required: true,
                    message: t('personalization.domain-is-required'),
                  },
                ]}
              >
                <Input placeholder="https://example.com.br" />
              </Item>
            </Col>
            <Divider style={{ opacity: 0.8, fontSize: '0.8rem' }}>
              Whatsapp
              <Tooltip title={t('personalization.whatsapp-help')}>
                <Button type="link" icon={<InfoCircleOutlined />} />
              </Tooltip>
            </Divider>
            <Col span={16}>
              <Item
                label={
                  <>
                    {t('personalization.number')}
                    <Tooltip title={t('personalization.number-help')}>
                      <Button type="link" icon={<InfoCircleOutlined />} />
                    </Tooltip>
                  </>
                }
                name="whatsapp"
              >
                <Input placeholder="55119888888" />
              </Item>
            </Col>
            <Col span={16}>
              <Item label={t('personalization.message')} name="whatsappMessage">
                <Input
                  style={{ width: '100%' }}
                  disabled={!whatsappNumber}
                  placeholder={t('personalization.message-placeholder')}
                />
              </Item>
            </Col>
          </Row>

          <Divider style={{ marginTop: 40 }} />
          <Row justify="end">
            <Button type="primary" htmlType="submit" disabled={isSaving}>
              {t(`personalization.${isSaving ? 'saving' : 'save'}`)}
            </Button>
          </Row>
        </Form>
      </CustomCard>
      <RoleChecker role={rolesConstants.MANAGE_SEND_EMAIL_CONFIGURATION}>
        <div style={{ marginTop: 16 }}>
          <SendEmails />
        </div>
      </RoleChecker>
    </>
  );
};

export default Personalization;
